import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery } from '@tanstack/react-query';
import {
  type ItineraryDetail,
  type ItineraryHotelSource,
  type SaveItineraryPlanRequest,
} from '@utazzo/shared';
import {
  UzAlert,
  UzButton,
  UzCheckbox,
  UzDateTimePicker,
  UzRadioGroup,
  UzSelect,
} from '@utazzo/ui';
import {
  AlertTriangle,
  ArrowRight,
  BedDouble,
  Building2,
  CalendarCheck,
  CalendarDays,
  Clock,
  GripVertical,
  Mail,
  MapPin,
  Phone,
  Plane,
  PlaneTakeoff,
  Plus,
  Route,
  Sparkles,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { useItineraryDays, useSaveItineraryPlan } from '../use-itineraries';

import { CruiseConfigCard, type CruiseStayRow } from './CruiseConfigCard';
import { FixedDepartureConfigurePanel } from './FixedDepartureConfigurePanel';
import { RoomsTravellersEditor } from './RoomsTravellersEditor';
import { useRoomPlan, familiesFromRoomGroups, roomGroupsPayload, type Family } from './useRoomPlan';

import { CityPicker, InsurancePickerDialog, VisaPickerDialog } from '@/components/pickers';
import * as countriesApi from '@/features/countries/countries.api';
import { useDestination } from '@/features/destinations/use-destinations';
import { useLead } from '@/features/leads/use-leads';
import { useVisaDetail } from '@/features/visas/use-visas';

/**
 * Step 1 - Configure. The itinerary is coupled to its lead: the lead form is
 * the source of truth for WHO is travelling, the marketing destination, and the
 * duration the client *asked* for (shown in the sidebar, not binding). The agent
 * builds the real plan: start date + departure city, an ordered draggable list
 * of city legs (city + nights + flight needed), and the room/family split.
 *
 * Saving persists everything (legs + families) and re-syncs the day rows, so the
 * step is fully editable when you come back: it hydrates from the saved plan.
 */

const uid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const addDays = (iso: string, n: number): string => {
  // Parse AND format in UTC so a timezone ahead of UTC (e.g. IST) doesn't roll
  // the date back a day when re-serialising via toISOString().
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const daysBetween = (a?: string | null, b?: string | null): number | null => {
  if (!a || !b) return null;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.round(ms / 86_400_000));
};

const fmtDate = (iso?: string | null): string | null =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

/** Today at local midnight - the earliest selectable start date. */
const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/** "YYYY-MM-DD" -> local Date (midnight), for UzDateTimePicker. */
const ymdToDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
/** Date -> "YYYY-MM-DD" (local). */
const dateToYmd = (d: Date | null): string =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : '';

interface Leg {
  id: string;
  cityId: string | null;
  /** Explicit stay dates (YYYY-MM-DD); null until the agent picks them. Each row
   *  is independent - adding a city never auto-fills these. Nights are derived
   *  from the pair only when both are set. */
  checkIn: string | null;
  checkOut: string | null;
  /** Per-city hotel-source override; null = follow the global source. */
  hotelSourceOverride?: ItineraryHotelSource | null;
}

/** Nights for a leg, derived from its own dates (0 until both dates are set). */
const legNights = (l: Pick<Leg, 'checkIn' | 'checkOut'>): number => {
  const n = daysBetween(l.checkIn, l.checkOut);
  return n && n > 0 ? n : 0;
};

/** One point-to-point flight leg: from -> to on a departure date. */
interface FlightLegRow {
  id: string;
  fromCityId: string | null;
  toCityId: string | null;
  /** Departure date (YYYY-MM-DD); null = not set. */
  departureDate?: string | null;
}

interface ConfigureStepProps {
  /**
   * Fixed departures (ADR-0069): the trip shape came from a template and the
   * land price was quoted for exactly it. The city-leg builder must not be
   * offered, or an agent can add a city the supplier never priced.
   */
  structureLocked?: boolean;
  itinerary: ItineraryDetail;
  /** Advance to the Days step after the first successful generate. */
  onGenerated: () => void;
  /** Reports unsaved-edit state up to the builder for the navigation guard. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Fired after an EDIT save (dates / rooms / ages changed on a plan that
   *  already has days). The builder uses it to force the Days tab to re-price +
   *  reconcile hotels for the new config on its next mount. */
  onSaved?: () => void;
  /**
   * When false (past / non-latest versions, or published status), every field
   * and the Save CTA lock. Defaults true so the draft-create flow keeps working.
   */
  editable?: boolean;
  /**
   * Optional override for the save action - the draft-create flow
   * (`/itineraries/new`) supplies its own combined create + plan-save
   * mutation so no DB row is written until "Generate day-by-day plan"
   * actually fires. When omitted, the standard `useSaveItineraryPlan`
   * hook owns the save (existing edit-mode behaviour).
   */
  saveOverride?: {
    mutate: (
      payload: SaveItineraryPlanRequest,
      opts: { onSuccess: () => void; onError?: (err: unknown) => void },
    ) => void;
    isPending: boolean;
  };
  /** Custom CTA label - used in draft mode to say "Generate day-by-day plan"
   *  unconditionally instead of switching between "Save changes" / "Generate". */
  saveCtaLabel?: string;
}

export const ConfigureStep = ({
  itinerary,
  onGenerated,
  onDirtyChange,
  onSaved,
  editable = true,
  structureLocked = false,
  saveOverride,
  saveCtaLabel,
}: ConfigureStepProps): React.JSX.Element => {
  const leadQuery = useLead(itinerary.leadId);
  const lead = leadQuery.data ?? null;

  const daysQuery = useItineraryDays(itinerary.id);
  const internalSave = useSaveItineraryPlan(itinerary.id);
  const save = saveOverride ?? internalSave;
  const alreadyHasDays = (daysQuery.data?.length ?? 0) > 0;

  const customerName =
    lead?.customer.primaryName ??
    lead?.customer.displayName ??
    itinerary.customerName ??
    'customer';
  // The final destination (not the marketing one) is what we surface + act on.
  const destination = lead?.destinationCity ?? null;
  // Lead is the source of truth for WHO is travelling. While the lead query
  // is still in flight, fall back to the itinerary's stored counts - but treat
  // `0` as "not yet set" (blank new-version rows default adultsCount to 0 in
  // Prisma). Using `??` alone would seed rooms with 0 adults, the agent would
  // bump the input to 1 to unlock Save, and savePlan would persist adultsCount=1
  // even though the lead asked for 2+. See 2026-07 adult-count-reset bug.
  const askedAdults =
    lead !== null && lead !== undefined
      ? lead.travellerAdults
      : itinerary.adultsCount > 0
        ? itinerary.adultsCount
        : 2;
  const askedChildren =
    lead !== null && lead !== undefined
      ? lead.travellerChildren
      : itinerary.childrenCount > 0
        ? itinerary.childrenCount
        : 0;
  const askedInfants =
    lead !== null && lead !== undefined
      ? lead.travellerInfants
      : itinerary.infantsCount > 0
        ? itinerary.infantsCount
        : 0;
  const clientNights = daysBetween(lead?.expectedTravelStart, lead?.expectedTravelEnd);
  const askedStart = fmtDate(lead?.expectedTravelStart);

  // The lead's FINAL destination scopes the city pickers. `lead.destinationCity`
  // was retargeted to the Destination master on 2026-06-24 (it's the
  // "Destination" dropdown, NOT a city). The Marketing Destination
  // (`lead.destination`) is intentionally ignored - we only act on the final one.
  const finalDestQuery = useDestination(lead?.destinationCity?.id ?? undefined);
  const destinationCountryId = finalDestQuery.data?.country?.id ?? null;
  const isDomesticTrip = finalDestQuery.data?.isInternational === false;

  // Departure cities default to India (most trips start there), but the picker
  // can still search any city worldwide.
  const indiaQuery = useQuery({
    queryKey: ['country-by-code', 'IN'],
    queryFn: () =>
      countriesApi.listCountries({ page: 1, pageSize: 10, sortDir: 'asc', search: 'India' }),
    staleTime: Infinity,
  });
  const indiaCountryId =
    indiaQuery.data?.data.find((c) => c.isoCode === 'IN')?.id ??
    indiaQuery.data?.data[0]?.id ??
    null;

  // ---- editable state (hydrated from the persisted itinerary, then the lead) --
  const [departureCityId, setDepartureCityId] = useState<string | null>(itinerary.fromCityId);
  // "Customer origin city" prefers the import snapshot
  // (`lead.leadDepartureCity` → DB `lead_departure_city`), then falls
  // back to itinerary.fromCityId / working departure. Never persisted
  // on the itinerary; never re-syncs when departure changes.
  const [customerOriginCityId, setCustomerOriginCityId] = useState<string | null>(null);
  const [hotelSource, setHotelSource] = useState<ItineraryHotelSource>(
    itinerary.hotelSource ?? 'TBO',
  );
  // Trip style shown on the customer docket ("Travel Details" section).
  // Defaults to Holiday & Leisure so the docket always carries a style.
  const [tripStyle, setTripStyle] = useState<string>(itinerary.tripStyle ?? 'Holiday & Leisure');
  // 2026-07-07. Visa: opt-in checkbox then the picker. Hidden for domestic
  // destinations where visa is not applicable.
  const [visaWanted, setVisaWanted] = useState<boolean>(Boolean(itinerary.visaId));
  const [visaId, setVisaId] = useState<string | null>(itinerary.visaId);
  // Insurance mirrors the visa checkbox + picker pattern. The persisted
  // `insuranceWanted` boolean is kept for legacy compatibility and is derived
  // from insuranceId presence on load.
  const [insuranceWanted, setInsuranceWanted] = useState<boolean>(
    itinerary.insuranceWanted || Boolean(itinerary.insuranceId),
  );
  const [insuranceId, setInsuranceId] = useState<string | null>(itinerary.insuranceId);
  const [legs, setLegs] = useState<Leg[]>(() => {
    if (itinerary.legs.length === 0) {
      return [{ id: uid(), cityId: null, checkIn: null, checkOut: null }];
    }
    // Persisted plans store contiguous nights + a single start date. Rebuild each
    // leg's explicit check-in/check-out from that so a saved itinerary re-opens
    // with the same dates it was saved with. Cruise stays are dated independently
    // (embarkDate), so when we lay the hotel legs out we must HOP the cursor over
    // any cruise-occupied dates - otherwise a hotel that follows a cruise re-opens
    // one night early and falsely overlaps it. Handles any hotel/cruise/hotel/
    // cruise ordering (the while loop re-checks every cruise after each hop).
    const start = itinerary.travelStartDate ?? null;
    const cruiseRanges = (itinerary.cruiseStays ?? []).map((c) => ({
      start: c.embarkDate,
      end: addDays(c.embarkDate, c.nights),
    }));
    const hopOverCruises = (iso: string): string => {
      let d = iso;
      let moved = true;
      while (moved) {
        moved = false;
        for (const r of cruiseRanges) {
          // ISO 'YYYY-MM-DD' strings compare correctly as plain strings.
          if (d >= r.start && d < r.end) {
            d = r.end;
            moved = true;
          }
        }
      }
      return d;
    };
    let cursor = start;
    return itinerary.legs.map((l) => {
      const src = l.hotelSourceOverride ?? null;
      if (!cursor)
        return {
          id: l.id,
          cityId: l.cityId,
          checkIn: null,
          checkOut: null,
          hotelSourceOverride: src,
        };
      const checkIn = hopOverCruises(cursor);
      const checkOut = addDays(checkIn, l.nights);
      cursor = checkOut;
      return { id: l.id, cityId: l.cityId, checkIn, checkOut, hotelSourceOverride: src };
    });
  });
  // Cruise stays (cruise-as-stay Phase 2) - independent of the hotel legs.
  // Mirrors a hotel leg exactly: just a city + dates, NO cruise picked here.
  // Hydrated from the persisted cruiseStays so a saved itinerary re-opens
  // with the same city/dates it was saved with.
  const [cruiseStayRows, setCruiseStayRows] = useState<CruiseStayRow[]>(() =>
    [...(itinerary.cruiseStays ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((c) => ({
        id: c.id,
        cityId: c.cityId,
        checkIn: c.embarkDate,
        checkOut: addDays(c.embarkDate, c.nights),
      })),
  );
  // Which component types this itinerary configures. Hydrated from the persisted
  // `configTypes` (null = pre-feature default: flights + hotels on, cruise on
  // only if the itinerary already has cruise stays). Unchecking a type suppresses
  // its auto-fill / placeholders / pricing and removes its placed components.
  const cfg = itinerary.configTypes;
  const [enableFlights, setEnableFlights] = useState(cfg?.flights ?? true);
  const [enableHotels, setEnableHotels] = useState(cfg?.hotels ?? true);
  const [enableCruise, setEnableCruise] = useState(
    cfg?.cruise ?? (itinerary.cruiseStays?.length ?? 0) > 0,
  );

  // Flight legs = explicit point-to-point routes (from -> to on a date),
  // independent of the hotel cities. Hydrated from the persisted flightLegs;
  // the first leg's `from` defaults to the departure city (editable).
  //
  // Autofill for departure dates: when a leg has no stored `departureDate` we
  // synthesise a sensible default so the operator isn't re-typing dates they
  // already set on the hotel plan.
  //   - first leg  -> itinerary.travelStartDate (outbound day = trip start)
  //   - last leg   -> itinerary.travelEndDate   (return = trip end)
  //   - middle legs stay blank; operator sets those explicitly.
  // The saved value (when non-null) always wins over the default.
  const [flightLegs, setFlightLegs] = useState<FlightLegRow[]>(() => {
    const stored = itinerary.flightLegs ?? [];
    const start = itinerary.travelStartDate ?? null;
    const end = itinerary.travelEndDate ?? null;
    return stored.map((f, i) => {
      let date = f.departureDate ?? null;
      if (!date) {
        if (i === 0) date = start;
        else if (i === stored.length - 1) date = end;
      }
      return {
        id: uid(),
        fromCityId: f.fromCityId,
        toCityId: f.toCityId,
        departureDate: date,
      };
    });
  });
  // The return leg is auto-seeded only ONCE. Without this guard, deleting a
  // leg drops the list back to length 1 and the seeding effect below instantly
  // re-creates the return - making the round-trip impossible to remove. The
  // effect flips this true as soon as the list has 2+ legs (seeded, persisted,
  // or manually added), so any later deletion sticks.
  const roundTripSeededRef = useRef(false);

  const initialFamilies: Family[] = familiesFromRoomGroups(itinerary.roomGroups);
  // Ignore empty / zero-pax persisted groups so we still seed from the lead's
  // asked counts (a blank version or a botched prior save must not lock the
  // room plan at 0 adults while the sidebar shows 2+).
  const initialPaxTotal = initialFamilies.reduce(
    (sum, f) =>
      sum + f.rooms.reduce((s, r) => s + (r.adults || 0) + (r.children || 0) + (r.infants || 0), 0),
    0,
  );
  const room = useRoomPlan(
    { adults: askedAdults, children: askedChildren, infants: askedInfants },
    initialFamilies.length > 0 && initialPaxTotal > 0 ? initialFamilies : undefined,
  );

  // One-time backfills from the lead (only when the field is still empty, so we
  // never clobber a saved value or an agent edit).
  const hadPersistedLegs = useRef(itinerary.legs.length > 0);
  useEffect(() => {
    if (!departureCityId && lead?.departureCity?.id) setDepartureCityId(lead.departureCity.id);
  }, [lead?.departureCity?.id, departureCityId]);
  // Snapshot customer origin once. Prefer import-captured
  // `leadDepartureCity`, then itinerary.fromCityId, then working
  // departure. Later departure edits do NOT re-sync.
  useEffect(() => {
    if (customerOriginCityId) return;
    const fromLead = lead?.leadDepartureCity?.id ?? null;
    const fromItinerary = itinerary.fromCityId;
    const next = fromLead ?? fromItinerary ?? departureCityId;
    if (next) setCustomerOriginCityId(next);
  }, [lead?.leadDepartureCity?.id, itinerary.fromCityId, departureCityId, customerOriginCityId]);
  // Seed ONLY the first stay from the lead (its expected start + asked duration),
  // and only while it's still untouched. Every other city stays blank - adding a
  // city never auto-fills dates; the agent sets each one manually.
  useEffect(() => {
    if (hadPersistedLegs.current) return;
    const start = lead?.expectedTravelStart ?? null;
    if (!start) return;
    setLegs((ls) => {
      const first = ls[0];
      if (ls.length !== 1 || !first || first.cityId || first.checkIn || first.checkOut) return ls;
      const nights = clientNights && clientNights > 0 ? clientNights : 3;
      return [{ ...first, checkIn: start, checkOut: addDays(start, nights) }];
    });
  }, [lead?.expectedTravelStart, clientNights]);

  // One-time autofill of per-child ages from the lead. Only for a fresh plan
  // (a persisted plan already carries its own saved ages, hydrated above). The
  // lead stores a single flat `childAges` array; we distribute it across the
  // seeded rooms in order (room 1 takes its children's ages first, etc.). Uses
  // a ref to the live room plan so the effect can fire once without depending
  // on the (re-created-every-render) plan object. setRoom here does NOT mark
  // the builder dirty (only `touch()` does), so the nav guard stays clean.
  const roomRef = useRef(room);
  roomRef.current = room;
  const agesSeededRef = useRef(false);
  useEffect(() => {
    if (agesSeededRef.current) return;
    // A persisted plan carries its own ages - never overwrite from the lead.
    if (initialPaxTotal > 0) {
      agesSeededRef.current = true;
      return;
    }
    const leadAges = lead?.childAges ?? [];
    if (leadAges.length === 0) return;
    const rp = roomRef.current;
    if (rp.assigned.children === 0) return; // rooms not seeded with children yet
    agesSeededRef.current = true;
    let cursor = 0;
    rp.families.forEach((fam) => {
      fam.rooms.forEach((r) => {
        if (r.children <= 0) return;
        // Missing lead ages stay null (empty box) - never a fabricated default.
        const ages = Array.from({ length: r.children }, (_, i) => leadAges[cursor + i] ?? null);
        cursor += r.children;
        rp.setRoom(fam.id, r.id, { childAges: ages });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.childAges, room.assigned.children, initialPaxTotal]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Mark the builder dirty on a user edit; entering this step starts clean.
  // True once the agent actually edits a field this session - gates the
  // post-save re-price so a no-op "Save changes" (nothing changed) doesn't
  // refetch hotels/flights on the Days tab.
  const editedRef = useRef(false);
  const touch = (): void => {
    editedRef.current = true;
    onDirtyChange?.(true);
  };
  useEffect(() => {
    onDirtyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Domestic destinations don't need visa - clear any stale selection.
  useEffect(() => {
    if (!isDomesticTrip) return;
    setVisaWanted(false);
    setVisaId(null);
  }, [isDomesticTrip]);

  // ---- route derived ----
  // Each stay owns its dates now, so totals come from the per-leg pairs and the
  // trip start is simply the first stay's check-in. The plan the API persists is
  // still contiguous (start + nights), which we assemble at save time.
  const totalNights = legs.reduce((s, l) => s + legNights(l), 0);
  const totalDays = totalNights + 1;
  const startDate = legs[0]?.checkIn ?? '';
  const endDate = legs.reduce(
    (acc, l) => (l.checkOut && (!acc || l.checkOut > acc) ? l.checkOut : acc),
    '',
  );
  const validLegs = legs.filter((l) => l.cityId && legNights(l) > 0);
  const validCruiseStays = cruiseStayRows.filter(
    (r) => r.cityId && r.checkIn && r.checkOut && daysBetween(r.checkIn, r.checkOut)! > 0,
  );
  // Trip start for the API payload: the EARLIEST of (every dated leg's
  // check-in, every cruise stay's embark date) - not just the earliest leg.
  // FIX C1 (final review): a cruise can embark before every hotel leg checks
  // in (e.g. fly in, cruise first, then a post-cruise hotel stay). The server
  // merges legs + cruise stays onto one calendar anchored on the earliest
  // segment of EITHER kind (see buildNightTimeline's `tripStart`), so the
  // payload's `startDate` must agree - anchoring on hotel legs alone made the
  // day-upsert loop (dated from `startDate`) disagree with the merged
  // timeline (indexed from the true, cruise-inclusive tripStart) whenever a
  // cruise embarked first.
  const planStartDate = (() => {
    const candidates = [
      ...validLegs.map((l) => l.checkIn).filter((d): d is string => Boolean(d)),
      ...validCruiseStays.map((r) => r.checkIn).filter((d): d is string => Boolean(d)),
    ];
    return candidates.length > 0 ? candidates.reduce((min, d) => (d < min ? d : min)) : '';
  })();

  // ---- unified dated timeline (cruise-as-stay Phase 2) ----
  // Hotel stays [checkIn, checkOut) + cruise stays [embark, embark+nights) live
  // on the SAME dated timeline. Any date-range overlap between a hotel stay
  // and a cruise stay (or two cruise stays) is a hard error - the day-merge
  // can't place two stays on the same day. Ranges are half-open, so a
  // boundary touch (one stay's checkout == the other's start) is NOT an
  // overlap.
  interface StayWindow {
    id: string;
    label: string;
    start: string;
    end: string;
  }
  const hotelWindows: StayWindow[] = validLegs.map((l, i) => ({
    id: l.id,
    label: `Hotel stay ${i + 1} (${fmtDate(l.checkIn)} - ${fmtDate(l.checkOut)})`,
    start: l.checkIn as string,
    end: l.checkOut as string,
  }));
  const cruiseWindows: StayWindow[] = validCruiseStays.map((r, i) => ({
    id: r.id,
    label: `Cruise stay ${i + 1} (${fmtDate(r.checkIn)} - ${fmtDate(r.checkOut)})`,
    start: r.checkIn as string,
    end: r.checkOut as string,
  }));
  const rangesOverlap = (a: StayWindow, b: StayWindow): boolean =>
    a.start < b.end && b.start < a.end;
  const timelineConflicts: { aId: string; bId: string }[] = [];
  // rowId -> full, ready-to-render message(s) for that row (overlap AND gap
  // messages share this map so both reuse the same red-border highlighting).
  const conflictLabels = new Map<string, string[]>();
  const addConflict = (a: StayWindow, b: StayWindow): void => {
    timelineConflicts.push({ aId: a.id, bId: b.id });
    conflictLabels.set(a.id, [
      ...(conflictLabels.get(a.id) ?? []),
      `Dates overlap with ${b.label}`,
    ]);
    conflictLabels.set(b.id, [
      ...(conflictLabels.get(b.id) ?? []),
      `Dates overlap with ${a.label}`,
    ]);
  };
  for (const h of hotelWindows) {
    for (const c of cruiseWindows) {
      if (rangesOverlap(h, c)) addConflict(h, c);
    }
  }
  for (let i = 0; i < cruiseWindows.length; i++) {
    for (let j = i + 1; j < cruiseWindows.length; j++) {
      const a = cruiseWindows[i]!;
      const b = cruiseWindows[j]!;
      if (rangesOverlap(a, b)) addConflict(a, b);
    }
  }

  // ---- gap detection (v1: gaps blocked, not just warned) ----
  // A GAP is a date within [tripStart, tripEnd) covered by neither a hotel
  // nor a cruise stay. `buildPlan` (auto-search.service.ts) re-derives each
  // hotel leg's placement by laying legs out CONTIGUOUSLY (hopping over any
  // interposed cruise stay), while `savePlan` honors the explicit checkIn
  // dates from `buildNightTimeline`. Those two derivations only agree when
  // the accepted itinerary is fully contiguous (no gaps) - so for v1 we
  // block gaps outright here instead of merely warning, guaranteeing
  // buildPlan's re-derived leg dates always match savePlan's day rows.
  // v1: gaps blocked (not just warned) so buildPlan's re-derived leg dates
  // match savePlan; gap-support needs ItineraryLeg.checkIn persisted +
  // threaded into buildPlan - deferred. (Intentional deviation from spec
  // decision #7's "gap = warning".)
  //
  // Scoped to CRUISE itineraries only: a pure hotel plan (no cruise stays)
  // stays on the untouched no-cruise server path, which ignores each leg's
  // displayed checkIn/checkOut entirely (nights are concatenated
  // contiguously from `startDate` regardless) - so a "gap" between two
  // hotel legs there is cosmetic only and has always been allowed. Gating
  // Save on it now would be a new, unrelated restriction on the long-standing
  // hotel-only workflow, and the HOTEL-ONLY invariant requires that path stay
  // untouched.
  const allStayWindows: StayWindow[] = [...hotelWindows, ...cruiseWindows];
  const gapRanges: { start: string; end: string }[] = [];
  if (validCruiseStays.length > 0 && allStayWindows.length > 0) {
    const sortedWindows = [...allStayWindows].sort((a, b) =>
      a.start < b.start ? -1 : a.start > b.start ? 1 : 0,
    );
    let coveredUntil = sortedWindows[0]!.start;
    let coveringWindow = sortedWindows[0]!;
    for (const w of sortedWindows) {
      if (w.start > coveredUntil) {
        gapRanges.push({ start: coveredUntil, end: w.start });
        const gapMessage = `Gap: no stay covers ${fmtDate(coveredUntil)} - ${fmtDate(w.start)}`;
        conflictLabels.set(coveringWindow.id, [
          ...(conflictLabels.get(coveringWindow.id) ?? []),
          gapMessage,
        ]);
        conflictLabels.set(w.id, [...(conflictLabels.get(w.id) ?? []), gapMessage]);
      }
      if (w.end > coveredUntil) {
        coveredUntil = w.end;
        coveringWindow = w;
      }
    }
  }
  const hasTimelineGap = gapRanges.length > 0;

  // ---- flight window ----
  // A ROUND TRIP is two or more flight legs that carry a departure date; the
  // latest of those is the return, i.e. the day the trip is over. No hotel stay
  // may run past it - the traveller has already flown home.
  //
  // A ONE-WAY plan (a single leg, or a return leg with no date yet) is left
  // completely alone: there is no return, so there is no end, and the traveller
  // may stay as long as they like. No warning, no save block.
  //
  // Dates are "YYYY-MM-DD" throughout this file, so plain string compare is
  // chronological (same trick the `endDate` reduce above uses).
  const flightDates = flightLegs
    .map((f) => f.departureDate)
    .filter((d): d is string => Boolean(d))
    .sort();
  const returnFlightDate = flightDates.length >= 2 ? (flightDates.at(-1) ?? null) : null;
  // The calendars stay fully open - an operator may pick any date they need.
  // This flag is purely advisory: it drives the warning banner and the save
  // block, so nothing is silently prevented, it's just called out.
  //
  // It fires on dates nobody typed, too: the first stay is autofilled from the
  // lead's asked duration (which knows nothing about the flights), and the
  // return leg's date is seeded once and never re-synced when the stays later
  // change - so the two drift apart on their own.
  const staysPastReturnFlight =
    returnFlightDate !== null &&
    legs.some(
      (l) =>
        (l.checkIn && l.checkIn > returnFlightDate) ||
        (l.checkOut && l.checkOut > returnFlightDate),
    );

  // Every child must have an age before the plan can be generated - it's
  // mandatory input (drives live hotel pricing), so an empty box blocks Save.
  const childAgesComplete = room.families.every((f) =>
    f.rooms.every((r) => {
      const ages = r.childAges ?? [];
      for (let i = 0; i < r.children; i++)
        if (ages[i] === null || ages[i] === undefined) return false;
      return true;
    }),
  );

  const canSave =
    editable &&
    Boolean(planStartDate) &&
    Boolean(departureCityId) &&
    validLegs.length > 0 &&
    room.assigned.adults > 0 &&
    childAgesComplete &&
    timelineConflicts.length === 0 &&
    !hasTimelineGap;

  // ---- leg ops ---- (each is a user edit -> mark dirty)
  const setLeg = (id: string, patch: Partial<Leg>): void => {
    touch();
    setLegs((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };
  const addLeg = (): void => {
    touch();
    // Blank dates - a newly added city is never auto-dated; the agent sets its
    // check-in / check-out by hand.
    setLegs((ls) => [...ls, { id: uid(), cityId: null, checkIn: null, checkOut: null }]);
  };
  const removeLeg = (id: string): void => {
    touch();
    setLegs((ls) => ls.filter((l) => l.id !== id));
  };

  // ---- cruise-stay ops ---- (cruise-as-stay Phase 2; independent of legs.
  // Mirrors leg ops exactly - "Add cruise stay" appends a blank row, no picker.)
  const addCruiseStay = (): void => {
    touch();
    setCruiseStayRows((rs) => [...rs, { id: uid(), cityId: null, checkIn: null, checkOut: null }]);
  };
  const removeCruiseStay = (id: string): void => {
    touch();
    setCruiseStayRows((rs) => rs.filter((r) => r.id !== id));
  };
  const changeCruiseStay = (
    id: string,
    patch: Partial<Pick<CruiseStayRow, 'cityId' | 'checkIn' | 'checkOut'>>,
  ): void => {
    touch();
    setCruiseStayRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  // ---- flight-leg ops ---- (from -> to on a date)
  const setFlightLeg = (id: string, patch: Partial<FlightLegRow>): void => {
    touch();
    setFlightLegs((xs) => {
      const idx = xs.findIndex((f) => f.id === id);
      if (idx < 0) return xs;
      const updated = xs.map((f, i) => (i === idx ? { ...f, ...patch } : f));
      // Chain invariant: every subsequent leg's departure city mirrors
      // the previous leg's arrival city. So a change to `toCityId`
      // cascades into the NEXT leg's `fromCityId` (which for a fully
      // built round-trip is the return leg - its origin now tracks the
      // 2nd-last leg's destination, exactly as the operator expects).
      if ('toCityId' in patch && idx + 1 < updated.length) {
        const nextLeg = updated[idx + 1]!;
        updated[idx + 1] = { ...nextLeg, fromCityId: patch.toCityId ?? null };
      }
      return updated;
    });
  };
  const addFlightLeg = (): void => {
    touch();
    setFlightLegs((xs) => {
      // First click seeds the outbound leg. Departs from the customer's
      // departure city on the trip start date (blank when the start isn't
      // set yet). The return leg is auto-seeded by an effect below once the
      // outbound has both cities picked - no click needed for it.
      if (xs.length === 0) {
        return [
          {
            id: uid(),
            fromCityId: departureCityId ?? null,
            toCityId: null,
            departureDate: startDate || null,
          },
        ];
      }
      // 2026-07-07. Round-trip UX: the last leg is treated as the return
      // (auto-seeded once the outbound is complete). Extra "Add flight leg"
      // clicks insert new intermediate legs BEFORE the return so the return
      // stays visually last. New leg chains from the leg immediately before
      // it (which is the previous forward leg). Operator can still edit the
      // return's cities/date if they need a non-mirrored itinerary.
      if (xs.length >= 2) {
        const beforeReturn = xs.slice(0, -1);
        const returnLeg = xs[xs.length - 1]!;
        const prev = beforeReturn[beforeReturn.length - 1] ?? null;
        return [
          ...beforeReturn,
          { id: uid(), fromCityId: prev?.toCityId ?? null, toCityId: null, departureDate: null },
          returnLeg,
        ];
      }
      // xs.length === 1 but effect hasn't seeded the return yet (outbound
      // still incomplete). Fall back to appending forward from the outbound.
      const prev = xs[xs.length - 1];
      return [
        ...xs,
        { id: uid(), fromCityId: prev?.toCityId ?? null, toCityId: null, departureDate: null },
      ];
    });
  };
  // 2026-07-07. Once the outbound leg has both cities picked, auto-seed the
  // return leg so the operator sees the full round-trip immediately (instead
  // of a hidden backend-computed return). Skips when a return already exists
  // (persisted flightLegs or manual "Add flight leg" click).
  useEffect(() => {
    // Any time there are already 2+ legs, the round-trip exists - lock seeding
    // so a later deletion back to 1 leg is never auto-undone.
    if (flightLegs.length >= 2) {
      roundTripSeededRef.current = true;
      return;
    }
    if (roundTripSeededRef.current) return;
    if (flightLegs.length !== 1) return;
    const first = flightLegs[0]!;
    if (!first.fromCityId || !first.toCityId) return;
    roundTripSeededRef.current = true;
    setFlightLegs((xs) => {
      if (xs.length !== 1) return xs;
      const only = xs[0]!;
      if (!only.fromCityId || !only.toCityId) return xs;
      return [
        ...xs,
        {
          id: uid(),
          fromCityId: only.toCityId,
          toCityId: only.fromCityId,
          // Return leg defaults to the trip end date so the operator sees the
          // round-trip laid out with concrete dates. `endDate` is derived from
          // the last hotel leg's checkout above (mirrors travelEndDate).
          departureDate: endDate || null,
        },
      ];
    });
  }, [flightLegs]);
  const removeFlightLeg = (id: string): void => {
    touch();
    // The operator is explicitly managing legs now - never auto-seed a return
    // again (otherwise a delete just bounces back).
    roundTripSeededRef.current = true;
    setFlightLegs((xs) => xs.filter((f) => f.id !== id));
  };
  const onDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    touch();
    setLegs((ls) => {
      const from = ls.findIndex((l) => l.id === active.id);
      const to = ls.findIndex((l) => l.id === over.id);
      return from < 0 || to < 0 ? ls : arrayMove(ls, from, to);
    });
  };

  const busy = save.isPending;
  // Past versions + published rows: freeze the whole form (fields + save).
  const locked = !editable || busy;

  const handleSave = (): void => {
    if (!canSave || !editable) return;
    // Hard block: a stay running past the return flight would generate
    // day-by-day rows for days the traveller is no longer in the country.
    // Deliberately NOT folded into `canSave` - that would grey the button out,
    // and a dead control can't explain itself. The button stays live so the
    // click has somewhere to land and the operator is told what to fix.
    if (staysPastReturnFlight) {
      toast.error('Please select hotel dates within the flight duration.');
      return;
    }
    save.mutate(
      {
        startDate: planStartDate,
        fromCityId: departureCityId,
        title: `Itinerary for ${customerName}`,
        hotelSource,
        // Which component types this itinerary configures. A disabled type is
        // not auto-filled / placed / priced (Hotels keeps the cities).
        configTypes: { flights: enableFlights, hotels: enableHotels, cruise: enableCruise },
        tripStyle,
        // Domestic trips never carry a visa add-on.
        visaId: isDomesticTrip ? null : visaWanted ? (visaId ?? null) : null,
        insuranceWanted,
        // Insurance product id (nullable). Server freezes the cost via
        // syncInsuranceCostPaise and folds it into the ADDON bucket.
        insuranceId: insuranceWanted ? (insuranceId ?? null) : null,
        // Explicit from -> to flight legs (only fully-filled rows).
        flightLegs: flightLegs
          .filter((f) => f.fromCityId && f.toCityId)
          .map((f) => ({
            fromCityId: f.fromCityId,
            toCityId: f.toCityId,
            departureDate: f.departureDate ?? null,
          })),
        legs: validLegs.map((l) => ({
          cityId: l.cityId as string,
          nights: legNights(l),
          // Dated-timeline anchor. FIX C1 (final review): the first leg's
          // check-in is NOT forced to equal `startDate` anymore - a cruise
          // stay may embark before it, in which case they legitimately
          // differ. Every leg's real checkIn is passed through as-is; the
          // server merges legs + cruise stays and anchors on the earliest of
          // ALL of them (buildNightTimeline's tripStart), not on this
          // `startDate` value.
          checkIn: l.checkIn as string,
          // Per-city hotel-source override; null = follow the global source.
          hotelSourceOverride: l.hotelSourceOverride ?? null,
        })),
        // Cruise stays configured alongside the route legs - independent of
        // `legs`. Just a city + dates, mirroring a hotel leg (NO cruise picked
        // here - the server auto-selects one for the city). Timeline overlap
        // with a hotel leg or another cruise stay is blocked client-side
        // (canSave), so every row here is conflict-free.
        cruiseStays: validCruiseStays.map((r) => ({
          cityId: r.cityId as string,
          embarkDate: r.checkIn as string,
          nights: daysBetween(r.checkIn, r.checkOut) as number,
        })),
        roomGroups: roomGroupsPayload(room.families),
      },
      {
        onSuccess: () => {
          onDirtyChange?.(false);
          if (!alreadyHasDays) onGenerated();
          // Editing an existing plan's config -> flag Days to re-price/reconcile
          // the hotels for the new dates/occupancy on its next mount. Only when
          // something was actually edited (a no-op save must not refetch).
          else if (editedRef.current) onSaved?.();
          editedRef.current = false;
        },
      },
    );
  };

  // A fixed departure's shape is contractual - the leg builder below would let
  // an agent add a city the supplier never priced (ADR-0069 D3).
  if (structureLocked) {
    return <FixedDepartureConfigurePanel itinerary={itinerary} editable={editable} />;
  }

  return (
    <div className="grid items-start gap-6 pb-20 lg:grid-cols-[minmax(0,1fr)_340px]">
      {/* ---- Left: the configuration the agent builds ---- */}
      <div className="min-w-0 space-y-6">
        <Card title="Trip basics">
          <div className="grid gap-5 sm:grid-cols-2">
            {/* 2026-07-05. Layout:
                  Row 1: Customer origin city | Trip style
                  Row 2: Start date | Departure city
                Origin prefers the lead's import-captured
                `leadDepartureCity` (customer origin), snapshotted once
                and then frozen. Rendered as a disabled CityPicker;
                never persisted on the itinerary. */}
            <CityPicker
              label="Customer origin city"
              value={customerOriginCityId}
              onValueChange={() => {
                /* no-op: the display is deliberately static */
              }}
              disabled
              hint="Captured from lead import (customer origin). Does not change when you edit the departure city."
            />
            <UzSelect
              label="Trip style"
              value={tripStyle}
              onValueChange={(v) => {
                touch();
                setTripStyle(v ?? 'Holiday & Leisure');
              }}
              options={[{ value: 'Holiday & Leisure', label: 'Holiday & Leisure' }]}
              hint='Shown in the "Travel Details" section of the customer page.'
            />
            <UzDateTimePicker
              mode="date"
              displayFormat="dd/MM/yyyy"
              placeholder="dd/mm/yyyy"
              label="Start date"
              minDate={startOfToday()}
              value={ymdToDate(startDate)}
              onValueChange={(d) => {
                touch();
                // The trip start is the first stay's check-in; keep that stay's
                // length when the start moves. Only the first city is affected.
                const iso = d ? dateToYmd(d) : null;
                setLegs((ls) => {
                  const first = ls[0];
                  if (!first) return ls;
                  const n = legNights(first);
                  const checkOut = iso && n > 0 ? addDays(iso, n) : first.checkOut;
                  return ls.map((l, i) => (i === 0 ? { ...l, checkIn: iso, checkOut } : l));
                });
              }}
              required
              disabled={locked}
            />
            <CityPicker
              label="Departure city"
              value={departureCityId}
              onValueChange={(v) => {
                touch();
                setDepartureCityId(v);
              }}
              preferCountryId={indiaCountryId}
              disabled={locked}
            />
          </div>
        </Card>

        {/* What to configure - which component types this itinerary sets up.
            Unchecking a type removes its placed components and stops its
            auto-fill + pricing. Hotels keeps the cities (they drive the day
            plan); only hotel booking is turned off. */}
        <Card
          title="What to configure"
          description="Pick the component types this itinerary needs. Unchecking one removes it and stops its auto-fill and pricing."
        >
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <UzCheckbox
              label="Flights"
              checked={enableFlights}
              onCheckedChange={(v) => {
                const on = v === true;
                touch();
                setEnableFlights(on);
                if (!on) setFlightLegs([]);
              }}
              disabled={locked}
            />
            <UzCheckbox
              label="Hotels"
              description="Cities still shape the trip when off"
              checked={enableHotels}
              onCheckedChange={(v) => {
                touch();
                setEnableHotels(v === true);
              }}
              disabled={locked}
            />
            <UzCheckbox
              label="Cruise"
              checked={enableCruise}
              onCheckedChange={(v) => {
                const on = v === true;
                touch();
                setEnableCruise(on);
                if (!on) setCruiseStayRows([]);
              }}
              disabled={locked}
            />
          </div>
        </Card>

        {/* Flight configuration - explicit point-to-point legs (from -> to on a
            date), any cities, independent of the hotel cities. */}
        {enableFlights && (
          <Card
            title="Flight configuration"
            description="Add each flight as a route: from city -> to city on a departure date. The first leg starts from your departure city."
          >
            <div className="space-y-2.5">
              {flightLegs.map((fl, idx) => {
                // Each leg's picker refuses dates earlier than the previous
                // leg's departure - you can't depart Delhi -> Bali on the 5th
                // if the Delhi -> Bali outbound is on the 10th. Walks back to
                // the closest previous leg that actually has a date set (an
                // operator may leave an intermediate blank); falls back to
                // today for the first leg.
                const prevDated = flightLegs
                  .slice(0, idx)
                  .reverse()
                  .find((f) => f.departureDate);
                const prevDate = prevDated ? ymdToDate(prevDated.departureDate ?? '') : null;
                const minDate = prevDate ?? startOfToday();
                return (
                  <div key={fl.id} className="flex items-end gap-2">
                    <div className="min-w-0 flex-1">
                      {/* 2026-07-05. Labels now render on EVERY row, not just
                      the first, for visual consistency. The old approach
                      hid them on rows 2+ which meant subsequent legs read
                      as an unlabeled grid of pickers. */}
                      <p className="mb-1 text-[10px] text-zinc-400 uppercase">From</p>
                      <CityPicker
                        label={undefined}
                        value={fl.fromCityId}
                        onValueChange={(v) => setFlightLeg(fl.id, { fromCityId: v })}
                        disabled={locked}
                      />
                    </div>
                    <ArrowRight className="mb-2.5 h-4 w-4 shrink-0 text-zinc-300" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="mb-1 text-[10px] text-zinc-400 uppercase">To</p>
                      <CityPicker
                        label={undefined}
                        value={fl.toCityId}
                        onValueChange={(v) => setFlightLeg(fl.id, { toCityId: v })}
                        preferCountryId={destinationCountryId}
                        disabled={locked}
                      />
                    </div>
                    <div className="w-36 shrink-0">
                      <p className="mb-1 text-[10px] text-zinc-400 uppercase">Departure</p>
                      <UzDateTimePicker
                        mode="date"
                        displayFormat="dd/MM/yyyy"
                        placeholder="Departure"
                        minDate={minDate ?? startOfToday()}
                        value={ymdToDate(fl.departureDate ?? '')}
                        onValueChange={(d) =>
                          setFlightLeg(fl.id, { departureDate: d ? dateToYmd(d) : null })
                        }
                        disabled={locked}
                      />
                    </div>
                    <UzButton
                      variant="ghost"
                      size="icon"
                      className="mb-0.5 h-9 w-9 text-zinc-400 hover:text-red-600"
                      onClick={() => removeFlightLeg(fl.id)}
                      disabled={locked}
                      aria-label="Remove flight leg"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </UzButton>
                  </div>
                );
              })}
              {flightLegs.length === 0 && (
                <p className="text-xs text-zinc-500">
                  No flights - add a route if the trip needs one.
                </p>
              )}
            </div>
            <UzButton
              variant="outline"
              size="sm"
              className="mt-1 h-7 text-xs"
              onClick={addFlightLeg}
              disabled={locked}
            >
              <Plane className="mr-1 h-3 w-3" aria-hidden />
              Add flight leg
            </UzButton>
          </Card>
        )}

        {/* Hotel configuration - draggable city legs (city + nights). The travel
            order comes from here. Stays visible even when hotel booking is off:
            the cities are the trip skeleton that drives the day-by-day plan. */}
        <Card
          title="Hotel configuration"
          description="Drag to set the order of travel. Each city's nights drive the day-by-day plan."
          headerRight={
            !enableHotels ? (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                Hotel booking off
              </span>
            ) : undefined
          }
        >
          {!enableHotels && (
            <UzAlert tone="info">
              <div>
                Hotel booking is off. The cities + nights below still shape the day-by-day plan, but
                no hotel is placed, priced, or auto-filled - and no airport-hotel transfers.
                Re-check Hotels above to book hotels for these cities.
              </div>
            </UzAlert>
          )}
          {enableHotels && (
            <UzRadioGroup
              label="Hotels"
              orientation="horizontal"
              options={[
                { value: 'PREFERRED', label: 'Preferred' },
                { value: 'TBO', label: 'Live' },
              ]}
              value={hotelSource}
              onValueChange={(v) => {
                touch();
                setHotelSource(v as ItineraryHotelSource);
              }}
              disabled={locked}
            />
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={legs.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2.5">
                {legs.map((leg, i) => {
                  // Stay-N hint: when the operator wants two hotels in the
                  // same city (e.g. "Bali 2N -> Bali 2N"), surface which
                  // stay this row is so the duplicated city reads clearly.
                  // Only shown when a city appears 2+ times.
                  const sameCityLegs = leg.cityId
                    ? legs.filter((l) => l.cityId === leg.cityId)
                    : [];
                  const stayHint =
                    sameCityLegs.length > 1
                      ? `Stay ${sameCityLegs.findIndex((l) => l.id === leg.id) + 1} of ${sameCityLegs.length}`
                      : null;
                  // A stay can't check in before the previous stay checks out.
                  // Walk back to the nearest earlier leg that has a check-out
                  // (an intermediate row may still be blank).
                  const prevCheckOut =
                    legs
                      .slice(0, i)
                      .reverse()
                      .find((l) => l.checkOut)?.checkOut ?? null;
                  return (
                    <SortableLeg
                      key={leg.id}
                      leg={leg}
                      index={i}
                      countryId={destinationCountryId}
                      disabled={locked}
                      canRemove={legs.length > 1}
                      checkIn={leg.checkIn ?? undefined}
                      checkOut={leg.checkOut ?? undefined}
                      minCheckIn={prevCheckOut ?? undefined}
                      stayHint={stayHint}
                      hasConflict={conflictLabels.has(leg.id)}
                      conflictMessage={(conflictLabels.get(leg.id) ?? []).join(', ')}
                      onChange={(patch) => setLeg(leg.id, patch)}
                      onRemove={() => removeLeg(leg.id)}
                      // Each stay's dates are independent - set only this row, no
                      // cascade to the trip start or neighbouring cities.
                      onCheckInChange={(d) => {
                        touch();
                        setLeg(leg.id, { checkIn: d });
                      }}
                      onCheckOutChange={(d) => {
                        touch();
                        setLeg(leg.id, { checkOut: d });
                      }}
                      globalHotelSource={hotelSource}
                      showHotelSource={enableHotels}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
          {/* Sits directly under the date rows it's about. It used to live at the
              very bottom of the page, below Rooms & travellers, where nobody
              scrolled far enough to see it. */}
          {editable && staysPastReturnFlight && (
            <UzAlert tone="warning" className="mt-2.5">
              {/* Single wrapping element on purpose. UzAlert's base class indents
                  every direct child element after the icon (`[&>svg~*]:pl-7`), so
                  a bare <strong> inline in the text would pick up that 1.75rem
                  itself and tear a gap mid-sentence. One block takes the indent. */}
              <div>Please select hotel dates within the flight duration.</div>
            </UzAlert>
          )}
          <UzButton
            variant="outline"
            size="sm"
            className="mt-1 h-7 text-xs"
            onClick={addLeg}
            disabled={locked}
          >
            <Plus className="mr-1 h-3 w-3" aria-hidden />
            Add city
          </UzButton>
        </Card>

        {/* Cruise configuration - a SEPARATE panel, sibling to Hotel
            configuration, but entered EXACTLY the same way: a city + dates,
            no cruise picked. Cruise stays live on the same dated timeline as
            the hotel legs above (checked for overlap below); occupancy is
            never entered here (Rooms & travellers owns that). */}
        {enableCruise && (
          <CruiseConfigCard
            rows={cruiseStayRows}
            disabled={locked}
            countryId={destinationCountryId}
            conflictLabels={conflictLabels}
            onAdd={addCruiseStay}
            onRemove={removeCruiseStay}
            onChange={changeCruiseStay}
          />
        )}

        {/* Rooms & families - the lead's asked pax is shown as a reference, but
            the agent may allocate freely (more or fewer travellers). */}
        <Card
          title="Rooms & travellers"
          description={
            room.isMultiFamily
              ? 'Split into families - each gets its own rooms and can be billed separately later.'
              : 'Everyone is in Family 1 by default. Add a family only when separate groups need their own rooms.'
          }
        >
          <RoomsTravellersEditor
            room={room}
            disabled={locked}
            showChildNoBed={hotelSource === 'PREFERRED'}
            onTouch={touch}
          />
        </Card>

        {!departureCityId && lead && (
          <UzAlert tone="warning">
            A <strong>departure city</strong> is required. Set it above, or{' '}
            <Link
              to={`/leads/${itinerary.leadId}`}
              rel="noopener noreferrer"
              className="text-primary font-medium underline"
            >
              complete the lead form
            </Link>
            .
          </UzAlert>
        )}

        {editable && !childAgesComplete && (
          <UzAlert tone="warning">
            Enter an <strong>age for every child</strong> in each room before generating the plan.
          </UzAlert>
        )}

        {editable && hasTimelineGap && (
          <UzAlert tone="destructive">
            <strong>Gap in the stay timeline</strong>: no hotel or cruise stay covers{' '}
            {gapRanges.map((g) => `${fmtDate(g.start)} - ${fmtDate(g.end)}`).join(', ')}. Every
            night of the trip must be covered by a stay - add a stay or adjust dates to close the
            gap.
          </UzAlert>
        )}
      </div>

      {/* ---- Right: read-only source of truth + summary ---- */}
      <aside className="space-y-5 self-start lg:sticky lg:top-8">
        <Card title="Customer" icon={<User className="text-primary h-4 w-4" aria-hidden />}>
          {leadQuery.isLoading ? (
            <SkeletonLines n={3} />
          ) : (
            <dl className="space-y-2.5">
              <SummaryRow
                label="Name"
                value={
                  lead?.customer.primaryName ?? lead?.customer.displayName ?? itinerary.customerName
                }
                icon={<User className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
              />
              <SummaryRow
                label="Email"
                value={lead?.customer.email}
                icon={<Mail className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
              />
              <SummaryRow
                label="Phone"
                value={lead?.customer.mobileNumber}
                icon={<Phone className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
              />
              {lead?.customer.companyName && (
                <SummaryRow
                  label="Company"
                  value={lead.customer.companyName}
                  icon={<Building2 className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
                />
              )}
            </dl>
          )}
        </Card>

        <Card
          title="Plan summary"
          icon={<MapPin className="text-primary h-4 w-4" aria-hidden />}
          description="What the client asked vs what's configured."
        >
          {leadQuery.isLoading ? (
            <SkeletonLines n={5} />
          ) : (
            <>
              <p className="mb-2 text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
                Requested
              </p>
              <dl className="space-y-2.5">
                <SummaryRow
                  label="Departure city"
                  value={lead?.departureCity?.name}
                  icon={<PlaneTakeoff className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
                />
                <SummaryRow
                  label="Destination"
                  value={destination?.name}
                  icon={<MapPin className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
                />
                <SummaryRow
                  label="Start date"
                  value={askedStart}
                  muted
                  icon={<CalendarDays className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
                />
                <SummaryRow
                  label="Duration"
                  value={clientNights ? `${clientNights}N · ${clientNights + 1}D` : 'Not specified'}
                  muted
                  icon={<Clock className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
                />
                <SummaryRow
                  label="Travellers"
                  value={`${askedAdults} Adult${askedAdults === 1 ? '' : 's'}${
                    askedChildren
                      ? ` · ${askedChildren} Child${askedChildren === 1 ? '' : 'ren'}`
                      : ''
                  }${askedInfants ? ` · ${askedInfants} Infant${askedInfants === 1 ? '' : 's'}` : ''}`}
                  icon={<Users className="h-3.5 w-3.5 text-zinc-400" aria-hidden />}
                />
              </dl>
            </>
          )}

          <div className="bg-accent/60 mt-3 rounded-md px-3 py-2.5">
            <p className="text-primary/70 mb-1.5 text-[10px] font-semibold tracking-wider uppercase">
              Configured
            </p>
            <dl className="space-y-1.5">
              <SummaryRow
                label="Route"
                value={`${totalNights}N · ${totalDays}D · ${validLegs.length} ${
                  validLegs.length === 1 ? 'city' : 'cities'
                }`}
                icon={<Route className="text-primary h-3.5 w-3.5" aria-hidden />}
              />
              <SummaryRow
                label="Start"
                value={fmtDate(startDate)}
                icon={<CalendarDays className="text-primary h-3.5 w-3.5" aria-hidden />}
              />
              <SummaryRow
                label="Ends"
                value={fmtDate(endDate)}
                icon={<CalendarCheck className="text-primary h-3.5 w-3.5" aria-hidden />}
              />
              <SummaryRow
                label="Rooms"
                value={`${room.totalRooms} · ${room.families.length} ${
                  room.families.length === 1 ? 'family' : 'families'
                }`}
                icon={<BedDouble className="text-primary h-3.5 w-3.5" aria-hidden />}
              />
            </dl>
          </div>
        </Card>

        {/* Add-ons: visa + insurance */}
        <Card title="Add-ons" icon={<Sparkles className="text-primary h-4 w-4" aria-hidden />}>
          <div className="space-y-4">
            {/* 2026-07-07. Visa: opt-in checkbox then the picker. Hidden when the
                lead's final destination is domestic. */}
            {!isDomesticTrip && (
              <div>
                <UzCheckbox
                  label="Visa required"
                  description="Only visas for the trip destination are shown."
                  checked={visaWanted}
                  onCheckedChange={(c) => {
                    touch();
                    const on = c === true;
                    setVisaWanted(on);
                    if (!on) setVisaId(null);
                  }}
                  disabled={locked}
                />
                {visaWanted && (
                  <div className="mt-3 space-y-2">
                    {/* Visa picker: country defaults to the trip destination
                      via `preferCountryId` but the dropdown stays enabled so
                      the operator can pick a visa from another country if
                      needed. Mirrors the insurance picker's behaviour. */}
                    <VisaPickerDialog
                      value={visaId}
                      initialLabel={itinerary.visaName}
                      preferCountryId={destinationCountryId}
                      onValueChange={(v) => {
                        touch();
                        setVisaId(v);
                      }}
                      disabled={locked}
                    />
                    <VisaProcessingModeNote visaId={visaId} />
                  </div>
                )}
              </div>
            )}
            <div>
              <UzCheckbox
                label="Travel insurance"
                description="Pick an insurance plan; cost flows into the quote via the ADDON bucket, same as visa."
                checked={insuranceWanted}
                onCheckedChange={(c) => {
                  touch();
                  const on = c === true;
                  setInsuranceWanted(on);
                  if (!on) setInsuranceId(null);
                }}
                disabled={locked}
              />
              {insuranceWanted && (
                <div className="mt-3 space-y-2">
                  <InsurancePickerDialog
                    value={insuranceId}
                    initialLabel={itinerary.insuranceName}
                    preferCountryId={destinationCountryId}
                    onValueChange={(v) => {
                      touch();
                      setInsuranceId(v);
                    }}
                    disabled={locked}
                  />
                </div>
              )}
            </div>
          </div>
        </Card>

        <div className="space-y-3">
          {!editable ? (
            <p className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
              This is a previous version - configure is view-only. Switch to the latest version to
              edit.
            </p>
          ) : (
            <UzButton className="w-full" onClick={handleSave} loading={busy} disabled={!canSave}>
              <Sparkles className="mr-1.5 h-4 w-4" aria-hidden />
              {saveCtaLabel ?? (alreadyHasDays ? 'Save changes' : 'Generate day-by-day plan')}
            </UzButton>
          )}
          {alreadyHasDays && (
            <UzButton variant="outline" className="w-full" onClick={onGenerated} disabled={busy}>
              Go to days
              <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
            </UzButton>
          )}
        </div>
      </aside>
    </div>
  );
};

// ---- sortable leg row ----

const SortableLeg = ({
  leg,
  index,
  countryId,
  disabled,
  canRemove,
  checkIn,
  checkOut,
  minCheckIn,
  stayHint,
  hasConflict,
  conflictMessage,
  onChange,
  onRemove,
  onCheckInChange,
  onCheckOutChange,
  globalHotelSource,
  showHotelSource,
}: {
  leg: Leg;
  index: number;
  countryId: string | null;
  disabled: boolean;
  canRemove: boolean;
  checkIn?: string;
  checkOut?: string;
  /** Earliest allowed check-in (previous stay's check-out); today when absent. */
  minCheckIn?: string;
  stayHint?: string | null;
  /** True when this stay overlaps another stay OR borders an unfilled gap
   *  in the timeline (unified hotel + cruise timeline). */
  hasConflict?: boolean;
  /** Full, ready-to-render conflict/gap message(s) for this stay, joined. */
  conflictMessage?: string;
  onChange: (patch: Partial<Leg>) => void;
  onRemove: () => void;
  onCheckInChange: (isoDate: string | null) => void;
  onCheckOutChange: (isoDate: string | null) => void;
  /** The itinerary's global hotel source, shown as the "follow global" option. */
  globalHotelSource: ItineraryHotelSource;
  /** Whether to show the per-city hotel-source override (hotels enabled). */
  showHotelSource: boolean;
}): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: leg.id,
    disabled,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        'rounded-lg border p-2.5 ' +
        (hasConflict ? 'border-red-300 bg-red-50/60' : 'border-zinc-200 bg-white')
      }
    >
      <div className="flex flex-wrap items-end gap-2.5">
        <button
          type="button"
          className="mb-1.5 cursor-grab touch-none text-zinc-300 hover:text-zinc-500 active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="bg-primary/10 text-primary mb-2 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold">
          {index + 1}
        </span>
        {/* 2026-07-05. Labels now render on EVERY row (was `index === 0` only).
            Keeps hotel-config visually consistent with flight-config +
            removes the "which column is what" guesswork on rows 2+. */}
        <div className="min-w-[120px] flex-1">
          <CityPicker
            label="City"
            value={leg.cityId}
            onValueChange={(v) => onChange({ cityId: v })}
            preferCountryId={countryId}
            disabled={disabled}
          />
          {stayHint && <p className="text-primary mt-1 text-[11px] font-medium">{stayHint}</p>}
        </div>
        <div className="w-[150px] shrink-0">
          <UzDateTimePicker
            mode="date"
            displayFormat="dd/MM/yyyy"
            placeholder="Check-in"
            label="Check-in"
            value={ymdToDate(checkIn ?? '')}
            minDate={(minCheckIn ? ymdToDate(minCheckIn) : undefined) ?? startOfToday()}
            onValueChange={(d) => {
              onCheckInChange(d ? dateToYmd(d) : null);
            }}
            disabled={disabled}
          />
        </div>
        <div className="w-[150px] shrink-0">
          <UzDateTimePicker
            mode="date"
            displayFormat="dd/MM/yyyy"
            placeholder="Check-out"
            label="Check-out"
            value={ymdToDate(checkOut ?? '')}
            minDate={checkIn ? (ymdToDate(addDays(checkIn, 1)) ?? undefined) : startOfToday()}
            onValueChange={(d) => {
              onCheckOutChange(d ? dateToYmd(d) : null);
            }}
            disabled={disabled || !checkIn}
          />
        </div>
        <span className="mb-2.5 hidden shrink-0 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600 tabular-nums sm:inline-block">
          {legNights({ checkIn: checkIn ?? null, checkOut: checkOut ?? null })}N
        </span>
        {showHotelSource && (
          <div className="w-[150px] shrink-0">
            <UzSelect
              label="Hotel source"
              value={leg.hotelSourceOverride ?? 'GLOBAL'}
              onValueChange={(v) =>
                onChange({
                  hotelSourceOverride: !v || v === 'GLOBAL' ? null : (v as ItineraryHotelSource),
                })
              }
              options={[
                {
                  value: 'GLOBAL',
                  label: `Global (${globalHotelSource === 'TBO' ? 'Live' : 'Preferred'})`,
                },
                { value: 'TBO', label: 'Live' },
                { value: 'PREFERRED', label: 'Preferred' },
              ]}
              disabled={disabled}
            />
          </div>
        )}
        <UzButton
          variant="ghost"
          size="icon"
          className="mb-0.5 h-9 w-9 text-zinc-400 hover:text-red-600"
          disabled={disabled || !canRemove}
          onClick={onRemove}
          aria-label="Remove city"
        >
          <Trash2 className="h-4 w-4" />
        </UzButton>
      </div>
      {hasConflict && (
        <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-600">
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
          {conflictMessage}
        </p>
      )}
    </div>
  );
};

// ---- presentational helpers ----

const Card = ({
  title,
  description,
  icon,
  headerRight,
  children,
}: {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  /** Optional control rendered at the right edge of the header (e.g. an enable checkbox). */
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element => (
  <section className="rounded-lg border border-zinc-200 bg-white">
    <header className="flex items-center gap-2 border-b border-zinc-100 px-5 py-3.5">
      {icon}
      <div className="flex-1">
        <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
      </div>
      {headerRight}
    </header>
    <div className="space-y-5 px-5 py-5">{children}</div>
  </section>
);

const SummaryRow = ({
  label,
  value,
  icon,
  muted,
}: {
  label: string;
  value?: string | null;
  icon?: React.ReactNode;
  muted?: boolean;
}): React.JSX.Element => (
  <div className="flex items-baseline justify-between gap-3">
    <dt className="flex items-center gap-1.5 text-xs text-zinc-500">
      {icon}
      {label}
    </dt>
    <dd
      className={
        'text-right text-sm font-medium ' +
        (value ? (muted ? 'text-zinc-500' : 'text-zinc-900') : 'text-zinc-400')
      }
    >
      {value || 'Not set'}
    </dd>
  </div>
);

const SkeletonLines = ({ n }: { n: number }): React.JSX.Element => (
  <div className="space-y-2.5">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className="h-4 animate-pulse rounded bg-zinc-100" />
    ))}
  </div>
);

/**
 * ADR-0062. Inline "Processing mode" summary under the visa picker.
 *   - No visa picked -> nothing rendered
 *   - Standard / Appointment only -> neutral zinc badge with the mode label
 *   - Appointment + Embassy       -> amber badge + "embassy fees at counter" note
 * The visa detail is fetched lazily via the picker's selected id so the
 * common no-visa case pays nothing.
 */
const VisaProcessingModeNote = ({
  visaId,
}: {
  visaId: string | null;
}): React.JSX.Element | null => {
  const visa = useVisaDetail(visaId ?? undefined).data ?? null;
  if (!visa) return null;
  const mode = visa.processingMode;
  const label =
    mode === 'APPOINTMENT_ONLY'
      ? 'Appointment only'
      : mode === 'APPOINTMENT_EMBASSY'
        ? 'Appointment + Embassy visit'
        : 'Standard (no appointment)';
  const isEmbassy = mode === 'APPOINTMENT_EMBASSY';
  return (
    <div
      className={
        'rounded-md px-3 py-2 text-xs ' +
        (isEmbassy ? 'bg-amber-50 text-amber-700' : 'bg-zinc-50 text-zinc-600')
      }
    >
      <p className="flex items-center gap-1.5">
        <span className="text-[10px] font-semibold tracking-wider uppercase opacity-70">
          Processing mode
        </span>
        <span className="font-medium">{label}</span>
      </p>
      {isEmbassy && (
        <p className="mt-1">
          Embassy fees directly payable at counter - not included in the visa price above.
        </p>
      )}
    </div>
  );
};
